import { useState, useEffect, useRef } from 'react';
import {
  Stack,
  Panel,
  PanelSection,
  FormLayout,
  FormLayoutGroup,
  Button,
  Banner,
  Link,
  Paragraph,
  useSnackbars,
  Box,
  Text,
  Spinner,
  Tag,
  Heading,
  Tooltip,
  Icon,
  EmptyState,
  EmptyStateTitle,
  EmptyStateDescription,
  EmptyStateActions,
} from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { useStore } from '@nanostores/react';
import { workspaceStore } from '@/services/auth/store';
import { appendMasqParam } from '@/utils/masq';
import { Form, useForm, TextArea } from '@/components/form';
import { isEmptyString } from '@/utils/string';
import { accountVerificationApi } from './api';
import { useCurrentWorkspace, getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { CUSTOM_INDUSTRY_OPTION_VALUE } from '@/features/business-profile-forms';
import { servicesQuery } from '@/services/api';
import { useQueryClient } from '@tanstack/react-query';
import type { AIContextOverrides } from '@/pages/journeys/settings/ai-business-profile/types';
import { BasicInfoFields, KeyLinksFields } from '@/features/business-profile-forms';
import { isAPIError } from '@/services/api/ts-rest';
import { sentryCaptureException } from '@/utils/sentry';
import { useVerifiedDomains, useAIProfileSelection } from './hooks';
import { accountVerificationFormValidators } from './utils';

const MIN_VALID_DATE = new Date('2020-01-01');

const cleanDomain = (domain: string): string => {
  return domain.replace(/^https?:\/\//, '');
};

const formatDateForTooltip = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
};

const getFormDefaultValues = (
  aiFormData:
    | {
        name?: string;
        websiteUrl?: string;
        industry?: string;
        customIndustry?: string;
        longDescription?: string;
        privacyPolicyUrl?: string;
        termsOfServiceUrl?: string;
      }
    | null
    | undefined,
  accountName?: string | null,
) => ({
  name: aiFormData?.name ?? accountName ?? '',
  websiteUrl: aiFormData?.websiteUrl ?? '',
  industry: aiFormData?.industry ?? '',
  customIndustry: aiFormData?.customIndustry ?? '',
  longDescription: aiFormData?.longDescription ?? '',
  privacyPolicyUrl: aiFormData?.privacyPolicyUrl ?? '',
  termsOfServiceUrl: aiFormData?.termsOfServiceUrl ?? '',
});

const trimIfNotEmpty = (value: string | undefined): string | undefined => {
  if (value == null) return undefined;
  const trimmed = value.trim();
  return !isEmptyString(trimmed) ? trimmed : undefined;
};

const masqLink = (url: string) => appendMasqParam(url);

export function AccountVerificationContent() {
  const auth = useAuth();
  const workspaces = useStore(workspaceStore);
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = getWorkspaceIdFromCurrentUrl();

  const snackbars = useSnackbars();
  const queryClient = useQueryClient();
  const navigate = useSafeNavigate();

  const { domains: verifiedDomains, isLoading: isLoadingDomains } = useVerifiedDomains(workspaces);
  const {
    selectedProfile,
    aiContext,
    aiFormData,
    isLoading: isLoadingProfile,
  } = useAIProfileSelection(workspaces, currentWorkspace?.id);

  const profileWorkspaceId = selectedProfile?.workspaceId ?? null;
  const workspaceIdForUpdate = profileWorkspaceId ?? workspaceId;

  const updateAIContextMutation = servicesQuery.updateAIContextOverrides.useMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const previousAiFormDataRef = useRef(aiFormData);

  const accountId = auth.account?.id != null ? String(auth.account.id) : null;

  const { data: currentJob, isLoading: isLoadingCurrentJob } = servicesQuery.verifyAccountCurrentJob.useQuery({
    queryKey: ['accountVerificationCurrentJob', accountId],
    queryData: {
      params: { accountId: accountId! },
    },
    enabled: accountId != null,
    retry: false,
    select: (data) => data?.body.job_id ?? null,
  });

  useEffect(() => {
    if (!isEmptyString(currentJob) && workspaceId != null) {
      navigate(`/workspaces/${workspaceId}/settings/actions/email/account-verification/status/${currentJob}`, {
        shouldReplaceHistoryEntry: true,
      });
    }
  }, [currentJob, workspaceId, navigate]);

  const isReadOnly = auth.user.account_permissions?.includes('account/edit') !== true;

  const updatedAt = aiContext?.updated_at;
  const hasValidUpdatedAt = updatedAt != null && typeof updatedAt === 'string' && !isEmptyString(updatedAt);
  const hasValidDomain = typeof aiContext?.domain === 'string' && !isEmptyString(aiContext.domain);
  const showGenerationInfo =
    aiContext != null && hasValidUpdatedAt && hasValidDomain && new Date(updatedAt) > MIN_VALID_DATE;

  const handleSubmit = async ({ value }: { value: ReturnType<typeof getFormDefaultValues> }) => {
    setIsSubmitting(true);
    try {
      if (accountId == null) {
        throw new Error('Account ID is required');
      }
      if (auth.user?.email == null) {
        throw new Error('User email is required');
      }
      const response = await accountVerificationApi.verifyAccount({
        accountId,
        email: auth.user.email,
        website: value.websiteUrl.trim(),
        privacyPolicyUrl: trimIfNotEmpty(value.privacyPolicyUrl),
        termsOfServiceUrl: trimIfNotEmpty(value.termsOfServiceUrl),
      });

      const jobId = response.job_id;
      if (jobId == null || isEmptyString(jobId)) {
        throw new Error('No job ID returned from verification request');
      }

      const hasValidWorkspace = workspaceIdForUpdate != null && workspaceIdForUpdate > 0;
      if (hasValidWorkspace) {
        try {
          const overrides: AIContextOverrides = {
            name: trimIfNotEmpty(value.name),
            website_url: trimIfNotEmpty(value.websiteUrl),
            industry:
              value.industry === CUSTOM_INDUSTRY_OPTION_VALUE
                ? trimIfNotEmpty(value.customIndustry)
                : trimIfNotEmpty(value.industry),
            long_description: trimIfNotEmpty(value.longDescription),
            privacy_policy_url: trimIfNotEmpty(value.privacyPolicyUrl),
            terms_of_service_url: trimIfNotEmpty(value.termsOfServiceUrl),
          };

          const filteredOverrides = Object.fromEntries(
            Object.entries(overrides).filter(([_, v]) => v != null && v !== ''),
          ) as AIContextOverrides;

          if (Object.keys(filteredOverrides).length > 0) {
            await updateAIContextMutation.mutateAsync({
              params: { workspaceId: workspaceIdForUpdate },
              body: { overrides: filteredOverrides },
            });
            queryClient.invalidateQueries({ queryKey: ['aiContextOverrides', workspaceIdForUpdate] });
          }
        } catch (error) {
          sentryCaptureException(error);
        }
      }

      if (workspaceId != null) {
        navigate(`/workspaces/${workspaceId}/settings/actions/email/account-verification/status/${jobId}`, {
          shouldReplaceHistoryEntry: false,
        });
      } else {
        snackbars.create({
          variant: 'error',
          id: 'account.verification.navigation.error',
          message: 'Unable to navigate to status page. Please refresh and try again.',
        });
        return;
      }
    } catch (error: unknown) {
      let errorMessage = 'An error occurred. Please contact win@customer.io for help verifying your account.';

      if (isAPIError(error)) {
        if (typeof error.message === 'string' && !isEmptyString(error.message)) {
          errorMessage = error.message;
        }
      }

      sentryCaptureException(error);

      snackbars.create({
        variant: 'error',
        id: 'account.verification.error',
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const form = useForm({
    defaultValues: getFormDefaultValues(aiFormData, auth.account?.name),
    validators: accountVerificationFormValidators,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (aiFormData != null && aiFormData !== previousAiFormDataRef.current) {
      if (!form.state.isDirty) {
        form.reset(getFormDefaultValues(aiFormData, auth.account?.name));
      }
      previousAiFormDataRef.current = aiFormData;
    }
  }, [aiFormData, auth.account?.name, form]);

  if (isLoadingProfile || isLoadingDomains || isLoadingCurrentJob) {
    return (
      <Box display="flex" justifyContent="center" p="400">
        <Spinner />
      </Box>
    );
  }

  if (verifiedDomains.length === 0) {
    const sendingDomainsPath =
      workspaceId != null ? `/workspaces/${workspaceId}/settings/actions/email/sending_domains` : null;

    return (
      <Panel>
        <PanelSection display="flex" justifyContent="center" alignItems="center" p="400">
          <EmptyState>
            <EmptyStateTitle icon="warning">No verified email domains found</EmptyStateTitle>
            <EmptyStateDescription>
              You need to verify at least one email domain before you can submit an account verification request.
            </EmptyStateDescription>
            {sendingDomainsPath != null ? (
              <EmptyStateActions>
                <Button variant="primary" href={sendingDomainsPath}>
                  Add and verify a domain
                </Button>
              </EmptyStateActions>
            ) : null}
          </EmptyState>
        </PanelSection>
      </Panel>
    );
  }

  return (
    <Box p="400">
      <Stack gap="400">
        <Panel>
          <PanelSection>
            <Form
              api={form}
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
            >
              <FormLayout>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb="300">
                  <Heading level="2">Verify your account to send emails</Heading>
                  {showGenerationInfo ? (
                    <Tooltip
                      content={`The information below was automatically generated by analyzing your sending domain ${cleanDomain(
                        aiContext.domain,
                      )} on ${formatDateForTooltip(
                        aiContext.updated_at,
                      )}. Our AI crawlers and enrichment pipeline gathered this data from your website and public sources. You can override any details that seem inaccurate — AI can sometimes hallucinate.`}
                      placement="bottom"
                      isCustom={true}
                    >
                      {(context) => (
                        <Box
                          ref={context.reference.setReference}
                          {...context.reference.getReferenceProps()}
                          display="inline-flex"
                          alignItems="center"
                          style={{ cursor: 'pointer' }}
                        >
                          <Icon name="info" color="subtle" />
                          <Text ml="100" color="subtle">
                            Where did this information come from?
                          </Text>
                        </Box>
                      )}
                    </Tooltip>
                  ) : null}
                </Box>
                <BasicInfoFields form={form} isDisabled={isReadOnly} autoFocus={true} />
                <TextArea
                  name="longDescription"
                  api={form}
                  label="Business description"
                  placeholder="Briefly describe what your business does and the value you provide"
                  minRows={4}
                  description="Explain what your business does, the products or services you offer, and the value you provide to customers"
                  isDisabled={isReadOnly}
                />
                <KeyLinksFields
                  form={form}
                  isDisabled={isReadOnly}
                  fields={['privacyPolicyUrl', 'termsOfServiceUrl']}
                />

                {workspaces != null && workspaces.length > 0 ? (
                  <Box display="inline-flex" flexDirection="column" style={{ width: 'fit-content' }}>
                    <Text as="label" weight="medium" mb="100">
                      Domains
                    </Text>
                    {isLoadingDomains ? (
                      <Box display="flex" alignItems="center" gap="100" mt="100">
                        <Spinner />
                        <Text>Loading domains...</Text>
                      </Box>
                    ) : verifiedDomains.length > 0 ? (
                      <Stack gap="100" mt="100">
                        {verifiedDomains.map((domain) => (
                          <Tag key={domain}>{domain}</Tag>
                        ))}
                      </Stack>
                    ) : (
                      <Text mt="100" color="subtle">
                        No verified domains found.
                      </Text>
                    )}
                  </Box>
                ) : null}

                <Banner variant="information" mt="200" mb="200">
                  <Paragraph>
                    By submitting this request, you are granting consent for our staff to access your account and data
                    in order to verify compliance with our{' '}
                    <Link href="https://customer.io/legal/terms-of-service" variant="secondary" isExternal>
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link href="https://customer.io/legal/acceptable-use-policy" variant="secondary" isExternal>
                      Acceptable Use Policy
                    </Link>
                    .
                    {workspaceIdForUpdate != null && workspaceIdForUpdate > 0 ? (
                      <>
                        {' '}
                        The information provided will also be saved to your{' '}
                        <Link
                          href={masqLink(`/workspaces/${workspaceIdForUpdate}/settings/ai-business-profile`)}
                          variant="secondary"
                        >
                          AI business profile
                        </Link>
                        .
                      </>
                    ) : null}
                  </Paragraph>
                </Banner>

                <FormLayoutGroup>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="primary"
                      isDisabled={isSubmitting || isReadOnly || form.state.isValid !== true}
                    >
                      {isSubmitting ? 'Submitting…' : 'Verify my account'}
                    </Button>
                  </Box>
                </FormLayoutGroup>
              </FormLayout>
            </Form>
          </PanelSection>
        </Panel>
      </Stack>
    </Box>
  );
}
